import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AuthScope } from "@playbookai/types";
import { useApiClient } from "../api/context.js";

const NO_SCOPE_SUBSCRIBE = (): (() => void) => () => undefined;
const NO_SCOPE_SNAPSHOT = (): null => null;

export function useSolveAuthScope(resourceId?: string): AuthScope | null {
  const config = useApiClient();
  const source = config.authScope;
  const scope = useSyncExternalStore(
    source?.subscribe ?? NO_SCOPE_SUBSCRIBE,
    source?.getSnapshot ?? NO_SCOPE_SNAPSHOT,
    source?.getSnapshot ?? NO_SCOPE_SNAPSHOT,
  );
  const queryClient = useQueryClient();
  const previous = useRef<{
    readonly scope: AuthScope | null;
    readonly resourceId: string | undefined;
  }>({ scope, resourceId });

  useEffect(() => {
    const prior = previous.current;
    previous.current = { scope, resourceId };
    if (
      !prior.scope ||
      (sameAuthScope(prior.scope, scope) && prior.resourceId === resourceId)
    ) {
      return;
    }
    const prefix = solveScopePrefix(prior.scope, prior.resourceId);
    void queryClient.cancelQueries({ queryKey: prefix });
    queryClient.removeQueries({ queryKey: prefix });
  }, [queryClient, resourceId, scope]);

  return scope;
}

export function useSolveMutationFence(): {
  readonly capture: () => AuthScope | null;
  readonly isCurrent: (scope: AuthScope | null) => scope is AuthScope;
} {
  const config = useApiClient();
  useSolveAuthScope();
  const capture = useCallback(
    () => config.authScope?.getSnapshot() ?? null,
    [config.authScope],
  );
  const isCurrent = useCallback(
    (scope: AuthScope | null): scope is AuthScope =>
      scope !== null &&
      sameAuthScope(scope, config.authScope?.getSnapshot() ?? null),
    [config.authScope],
  );
  return { capture, isCurrent };
}

export function solveScopePrefix(
  scope: AuthScope,
  resourceId?: string,
): readonly unknown[] {
  return [
    "resource-access",
    scope.tenantId,
    scope.actorUserId,
    scope.authSessionGeneration,
    "solve",
    ...(resourceId ? [resourceId] : []),
  ] as const;
}

export function solveDetailQueryKey(
  scope: AuthScope,
  solveId: string,
): readonly unknown[] {
  return [...solveScopePrefix(scope, solveId), "detail"] as const;
}

/**
 * Refresh every open SOLVE's detail read after content changed under it
 * (PB-1570).
 *
 * A saved widget edit moves the SOLVE's `updatedAt` server-side, but nothing
 * told the client: `SolveWorkspace` invalidates the detail query only when
 * `currentPhase` changes, so the workspace kept rendering "Last updated 1m
 * ago" over an edit that had just persisted — correct data under a stale
 * timestamp, which is exactly the reading that makes someone doubt their save
 * landed.
 *
 * A PREDICATE rather than an exact key, because the two call sites — the
 * widget resolver's save and the peer `widget_changed` nudge — do not both
 * have the auth scope in hand, and the resolver is mounted once for every
 * module and holds no SOLVE identity at all. It is narrow enough to be cheap:
 * it matches SOLVE detail reads only, and a session has at most a handful of
 * SOLVE workspaces open.
 *
 * Deliberately NOT invalidating the widget entity itself — the caller either
 * just wrote it (and holds the server echo) or is deciding separately whether
 * a peer's nudge should stomp a local edit.
 */
export function invalidateSolveFreshness(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === "resource-access" &&
        key[4] === "solve" &&
        key[6] === "detail"
      );
    },
  });
}

export function sameAuthScope(
  left: AuthScope,
  right: AuthScope | null,
): boolean {
  return (
    right !== null &&
    left.tenantId === right.tenantId &&
    left.actorUserId === right.actorUserId &&
    left.authSessionGeneration === right.authSessionGeneration
  );
}
